import { Flex, Text } from "styles/utility-components";

import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import { MultiSelectDropdown, Option } from "lib/core_components/MultiSelectDropdownList";
import { RELEVANCY_SEARCH, RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { SavedViewDataType } from "lib/types/graphQLEnums";
import { capitalize } from "lib/utils";

type DataTypeDropdownFilterProps = {
  onChange: (value: SavedViewDataType[]) => void;
  value: SavedViewDataType[];
};

export function DataTypeDropdownFilter({ onChange, value }: DataTypeDropdownFilterProps) {
  // A lot of the complexity in the dropdown filter below can be removed alongside the RELEVANCY_SEARCH_NEW_USERS flag
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH, false);
  const isRelevancySearchNewUsersEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const excludedType = isRelevancySearchNewUsersEnabled
    ? SavedViewDataType.Notice
    : isRelevancySearchEnabled
      ? null
      : SavedViewDataType.NoticeSemantic;

  const availableTypes = Object.values(SavedViewDataType).filter((t) => t !== excludedType);

  const dataTypeOptions: Option<SavedViewDataType>[] = availableTypes.map((type) => {
    const label = getSelectDropdownLabel([type], isRelevancySearchNewUsersEnabled);
    return {
      title: label,
      value: type,
      OptionComponent: (
        <Flex gap={9} alignItems="center" key={type} minWidth={200} height={37}>
          <span>{label}</span>
        </Flex>
      ),
    };
  });

  const label = getSelectDropdownLabel(
    value,
    isRelevancySearchNewUsersEnabled,
    availableTypes.length,
  );

  return (
    <MultiSelectDropdown
      title={
        // A title that is not plain text skips Button's own ellipsis, so it has to clamp
        // itself or it wraps out of the trigger. The tooltip only fires when it truncates.
        <EllipsisTooltip fullText={label}>
          {(ref) => (
            <Text ref={ref} color="inherit" ellipsis>
              {label}
            </Text>
          )}
        </EllipsisTooltip>
      }
      fieldName="datatype"
      searchable
      hideSelectAll
      skipSort
      options={dataTypeOptions}
      selectedOptions={value}
      onChange={onChange}
      showSelectedSizeBadge={false}
    />
  );
}

function getSelectDropdownLabel(
  values: SavedViewDataType[],
  isRelevancySearchNewUsersEnabled: boolean,
  totalAvailableOptions?: number,
): string {
  if (values.length === 0) return "All";

  if (values.length === 1) {
    const value = values[0];
    if (value === SavedViewDataType.NoticeSemantic) {
      return isRelevancySearchNewUsersEnabled ? "Notices" : "Notices v2";
    }
    return `${capitalize(value)}s`;
  }

  if (totalAvailableOptions !== undefined && values.length === totalAvailableOptions) {
    return "All";
  }

  return `${values.length} types`;
}
